type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
interface RequestParams {
  method?: Method;
  body?: object | string;
  headers?: Headers;
  signal?: AbortSignal;
}

export type RequestErrorResponse = {
  error: string;
  status: number;
};

class CustomError extends Error {
  cause?: {
    status?: number;
  };

  constructor(message: string, cause?: { status?: number }) {
    super(message);
    this.cause = cause;
  }
}

async function getJSON(response: Response) {
  if (response.status === 204) return null;
  return response.json();
}

export async function request<T>(
  url: string,
  params?: RequestParams,
  tags?: string[]
): Promise<T> {
  const requestHeaders = new Headers(params?.headers);

  if (!requestHeaders.has('Content-Type')) {
    requestHeaders.append('Content-Type', 'application/json');
  }

  const options: RequestInit = {
    method: params?.method || 'GET',
    headers: requestHeaders,
    body: params?.body && JSON.stringify(params.body),
    signal: params?.signal,
  };

  if (tags) {
    options.next = { tags };
  }

  const response = await fetch(url, options);

  const data = await getJSON(response);
  if (!response.ok) {
    if ('causes' in data) {
      throw new CustomError(data.causes, {
        status: response.status,
      });
    }
    throw new CustomError(response.statusText, {
      status: response.status,
    });
  }

  return data;
}
export async function withErrorHandler<T>(
  fn: (params?: RequestParams) => Promise<T>,
  params?: RequestParams
): Promise<T | RequestErrorResponse> {
  try {
    const response = await fn(params);
    return response;
  } catch (error) {
    const enhancedError: RequestErrorResponse = {
      error: error instanceof Error ? error.message : 'Unknown error',
      status: 500,
    };

    if (error instanceof CustomError) {
      enhancedError.status = error.cause?.status || 500;
    }

    return enhancedError;
  }
}
